import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import _ from 'lodash';
import { sqPdfExportStore } from '@/core/core.stores';
import { formatMessage } from '@/utilities/logger.utilities';
import { errorToast } from '@/utilities/toast.utilities';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import type { DocumentLayout, DocumentMarginUnits, DocumentPaperSize, Margin } from '@/reportEditor/report.constants';

export function setShowModal(showModal: boolean) {
  sqPdfExportStore.actions.setShowModal(showModal);
}

export async function setPageLayout(layout: DocumentLayout, itemId: string | undefined) {
  await updatePDFSettingAndDispatch(
    (value) => sqPdfExportStore.actions.setLayout(value),
    SeeqNames.Properties.Orientation,
    layout,
    itemId,
  );
}

export async function setPaperSize(paperSize: DocumentPaperSize, itemId: string | undefined) {
  await updatePDFSettingAndDispatch(
    (value) => sqPdfExportStore.actions.setPaperSize(value),
    SeeqNames.Properties.PageSize,
    paperSize,
    itemId,
  );
}

export async function setTopMargin(value: number | string, unit: DocumentMarginUnits, itemId: string | undefined) {
  await updatePDFSettingAndDispatch(
    (value) => sqPdfExportStore.actions.setMarginTop(value),
    SeeqNames.Properties.MarginTop,
    {
      value,
      units: unit,
    } as Margin,
    itemId,
  );
}

export async function setBottomMargin(value: number | string, unit: DocumentMarginUnits, itemId: string | undefined) {
  await updatePDFSettingAndDispatch(
    (value) => sqPdfExportStore.actions.setMarginBottom(value),
    SeeqNames.Properties.MarginBottom,
    {
      value,
      units: unit,
    } as Margin,
    itemId,
  );
}

export async function setLeftMargin(value: number | string, unit: DocumentMarginUnits, itemId: string | undefined) {
  await updatePDFSettingAndDispatch(
    (value) => sqPdfExportStore.actions.setMarginLeft(value),
    SeeqNames.Properties.MarginLeft,
    {
      value,
      units: unit,
    } as Margin,
    itemId,
  );
}

export async function setRightMargin(value: number | string, unit: DocumentMarginUnits, itemId: string | undefined) {
  await updatePDFSettingAndDispatch(
    (value) => sqPdfExportStore.actions.setMarginRight(value),
    SeeqNames.Properties.MarginRight,
    {
      value,
      units: unit,
    } as Margin,
    itemId,
  );
}

function validatePageMargin(value: number | string, unit: DocumentMarginUnits) {
  const num = _.isString(value) ? parseFloat(value) : value;
  if (!_.isFinite(num) || num < 0) {
    throw new TypeError(formatMessage`'${value}' is not a valid margin value`);
  }
  const margin = value + unit;
  return {
    stringValue: margin,
    margin: { value: num, units: unit } as Margin,
  };
}

function isMargin(potentialMargin: Margin | DocumentPaperSize | DocumentLayout): potentialMargin is Margin {
  return !_.isUndefined((potentialMargin as Margin).units);
}

async function updatePDFSettingAndDispatch<T extends Margin | DocumentPaperSize | DocumentLayout>(
  storeAction: (value: T) => void,
  propertyName: string,
  pageSetting: T,
  itemId: string | undefined,
) {
  let dispatchValue: Margin | DocumentPaperSize | DocumentLayout = pageSetting;
  let propertyValue: string;

  if (isMargin(pageSetting)) {
    const { stringValue, margin } = validatePageMargin(pageSetting.value, pageSetting.units);
    dispatchValue = margin;
    propertyValue = stringValue;
  } else {
    propertyValue = pageSetting.value;
  }
  if (itemId) {
    try {
      await sqItemsApi.setProperty({ value: propertyValue }, { id: itemId, propertyName }, { ignoreLoadingBar: true });
    } catch (e) {
      errorToast({ httpResponseOrError: e });
    }
  }
  storeAction(dispatchValue as T);
}

/** this function is used to set the layout to Landscape for Dashboards
 * It does NOT trigger a PDF creation request and is done every time a Dashboard is opened.
 */
export function setDefaultPageLayout(layout: DocumentLayout) {
  sqPdfExportStore.actions.setLayout(layout);
}
